import { getWindow } from './windowUtils'

type PathScopeUpdates = {
  /** If provided, replaces the `:domain` segment in `/domain/:domain/...` */
  domainId?: string
  /** If provided, replaces the `:projectId` segment in `/project/:projectId/...` */
  projectId?: string
}

/** Collapse consecutive `/` characters in a pathname. */
export function collapseDuplicatePathSlashes(pathname: string): string {
  return pathname.replace(/\/{2,}/g, '/')
}

/**
 * Normalize `//` in the current URL before Next.js App Router hydrates.
 *
 * Hosted proxies can serve the app for URLs like `https://host//v2/...`
 * without redirecting, leaving a leading `//` in `location.pathname`. The
 * History API treats that as protocol-relative (`//v2/x` → `https://v2/x`),
 * so Next's initial `history.replaceState` throws a SecurityError and the
 * page crashes into its error boundary.
 *
 * Called from `src/instrumentation-client.ts`, which is part of the client
 * runtime and therefore executes even on Next's client-side-render bailout
 * pages — those omit the root layout's `<head>`, so a `<head>` script cannot
 * cover them. Interim mitigation until the edge proxy 308-redirects `//` URLs
 * (see double-slash-redirect-infra-handoff.md); remove once that ships.
 */
export function normalizeLocationPathSlashes(
  loc?: Pick<Location, 'pathname' | 'search' | 'hash'>,
  hist?: Pick<History, 'replaceState' | 'state'>,
): void {
  try {
    const w = getWindow()
    const location = loc ?? w?.location
    const history = hist ?? w?.history
    if (!location || !history) return
    const pathname = location.pathname
    if (!pathname.includes('//')) return
    const normalized = collapseDuplicatePathSlashes(pathname)
    if (normalized === pathname) return
    // Pass current state through: on reload/session restore the browser has
    // already restored Next's framework state, and `null` would wipe it.
    history.replaceState(
      history.state,
      '',
      normalized + location.search + location.hash,
    )
  } catch {
    // Bootstrap hardening must never break page load.
  }
}

/**
 * Returns a pathname updated with the provided scope changes (domain and/or project).
 *
 * Purpose:
 * - When the user switches **domain** and/or **project**, keep them on the same
 *   list-level section when possible.
 * - If the current route appears to be a **detail page** within a section
 *   (i.e. it has extra segments after the section), trim back to the section list.
 *
 * Examples:
 * - `/.../domain/old/project/p1/runs` + { domainId: 'new' } -> `/.../domain/new/project/p1/runs`
 * - `/.../domain/old/project/p1/runs/abc123` + { domainId: 'new' } -> `/.../domain/new/project/p1/runs`
 * - `/.../domain/d1/project/old/tasks/t42` + { projectId: 'newP' } -> `/.../domain/d1/project/newP/tasks`
 */
export const getUpdatedScopedPath = (
  pathName: string,
  updates: PathScopeUpdates,
) => {
  // `usePathname()` returns a pathname without the origin and without query string.
  const segments = pathName.split('/').filter(Boolean)

  // Update /domain/:domainId
  if (updates.domainId) {
    const domainIdx = segments.indexOf('domain')
    if (domainIdx !== -1 && segments[domainIdx + 1]) {
      segments[domainIdx + 1] = updates.domainId
    } else {
      // If we couldn't find a domain segment, fall back to regex replacement.
      // (Keeps behavior stable for unexpected route shapes.)
      return pathName.replace(/\/domain\/[^/]+/, `/domain/${updates.domainId}`)
    }
  }

  // Update /project/:projectId
  if (updates.projectId) {
    const projectIdx = segments.indexOf('project')
    if (projectIdx !== -1 && segments[projectIdx + 1]) {
      segments[projectIdx + 1] = updates.projectId
    } else {
      // If we couldn't find a project segment, fall back to regex replacement.
      return pathName.replace(
        /\/project\/[^/]+/,
        `/project/${updates.projectId}`,
      )
    }
  }

  // If we're on a project list/detail route, trim any detail segments back to the list.
  // Expected shapes:
  // - /.../domain/:domain/project/:projectId/:section
  // - /.../domain/:domain/project/:projectId/:section/:resourceId(/...)
  const projectIdx = segments.indexOf('project')
  const sectionIdx = projectIdx === -1 ? -1 : projectIdx + 2 // after `project` + `:projectId`

  if (sectionIdx !== -1 && segments[sectionIdx]) {
    // If there are segments after the section, we're on a detail page; trim to list page.
    if (segments.length > sectionIdx + 1) {
      segments.splice(sectionIdx + 1)
    }
  }

  return `/${segments.join('/')}`
}

/** Convenience wrapper for domain changes (backwards-compatible with prior usage). */
export const getUpdatedDomainPath = (pathName: string, newDomain: string) =>
  getUpdatedScopedPath(pathName, { domainId: newDomain })

/** Convenience wrapper for project changes. */
export const getUpdatedProjectPath = (pathName: string, newProjectId: string) =>
  getUpdatedScopedPath(pathName, { projectId: newProjectId })

/**
 * Returns the AWS S3 Console URL for an s3:// URI, or null if the string is not a valid s3 URI.
 *
 * @param uri - The s3:// URI (e.g. from a blob's uri field).
 * @param options.isDirectory - When true, the URI is a multipart blob (directory/prefix); open the
 *   bucket browser at that prefix with a trailing slash. When false or omitted, uses heuristics.
 *
 * Directory format: prefix with literal slashes and trailing slash, showversions=false.
 *
 * @see https://docs.aws.amazon.com/AmazonS3/latest/userguide/view-object-overview.html
 * @see https://docs.aws.amazon.com/AmazonS3/latest/userguide/using-storagebrowser.html
 */
export function getS3ConsoleUrl(
  uri: string | undefined,
  options?: { isDirectory?: boolean },
): string | null {
  if (!uri || typeof uri !== 'string') return null
  const trimmed = uri.trim()
  const match = trimmed.match(/^s3:\/\/([^/]+)(?:\/(.*))?$/)
  if (!match) return null
  const [, bucket, key] = match
  if (!bucket) return null
  const keyPart = key ?? ''
  const base = 'https://s3.console.aws.amazon.com'
  const encodedBucket = encodeURIComponent(bucket)
  const isDirectory =
    options?.isDirectory === true || keyPart === '' || keyPart.endsWith('/')

  if (isDirectory) {
    if (keyPart === '') {
      return `${base}/s3/buckets/${encodedBucket}?showversions=false`
    }
    const prefixForDir = keyPart.endsWith('/') ? keyPart : `${keyPart}/`
    const prefixEncoded = encodeURIComponent(prefixForDir).replace(/%2F/g, '/')
    return `${base}/s3/buckets/${encodedBucket}?prefix=${prefixEncoded}&showversions=false`
  }
  const encodedKey = encodeURIComponent(keyPart)
  return `${base}/s3/object/${encodedBucket}?prefix=${encodedKey}`
}

/**
 * Returns the Google Cloud Storage Console URL for a gs:// URI, or null if the string is not a valid gs URI.
 *
 * @param uri - The gs:// URI (e.g. gs://bucket-name/path/to/object or gs://bucket-name/prefix/).
 * @param options.isDirectory - When true, the URI is a directory prefix; open the bucket browser at that prefix.
 *
 * @see https://cloud.google.com/storage/docs/cloud-console
 */
export function getGcsConsoleUrl(
  uri: string | undefined,
  options?: { isDirectory?: boolean },
): string | null {
  if (!uri || typeof uri !== 'string') return null
  const trimmed = uri.trim()
  const match = trimmed.match(/^gs:\/\/([^/]+)(?:\/(.*))?$/)
  if (!match) return null
  const [, bucket, key] = match
  if (!bucket) return null
  const keyPart = key ?? ''
  const base = 'https://console.cloud.google.com/storage/browser'
  const encodedBucket = encodeURIComponent(bucket)
  const isDirectory =
    options?.isDirectory === true || keyPart === '' || keyPart.endsWith('/')

  if (keyPart === '') {
    return `${base}/${encodedBucket}`
  }
  const prefix = isDirectory && !keyPart.endsWith('/') ? `${keyPart}/` : keyPart
  const prefixEncoded = encodeURIComponent(prefix).replace(/%2F/g, '/')
  return `${base}/${encodedBucket}?prefix=${prefixEncoded}`
}

/**
 * Returns the cloud storage console URL for an s3:// or gs:// URI.
 * Tries S3 first, then GCS; returns null if the URI is neither.
 *
 * @param uri - An s3:// or gs:// URI (e.g. from a blob's uri or path field).
 * @param options.isDirectory - When true, open the bucket browser at that prefix (for directories/dataframes).
 */
export function getStorageConsoleUrl(
  uri: string | undefined,
  options?: { isDirectory?: boolean },
): string | null {
  return getS3ConsoleUrl(uri, options) ?? getGcsConsoleUrl(uri, options)
}
